import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  emailTemplateService,
  EmailTemplateListFilters,
  EmailTemplatePayload,
} from '@/services/emailTemplate.service';
import { QUERY_KEYS } from '@/utils/constants';

export const useEmailTemplates = (filters?: EmailTemplateListFilters) =>
  useQuery({
    queryKey: [...QUERY_KEYS.EMAIL_TEMPLATES.ALL, filters],
    queryFn: () => emailTemplateService.getAll(filters),
  });

export const useEmailTemplate = (id: number) =>
  useQuery({
    queryKey: QUERY_KEYS.EMAIL_TEMPLATES.BY_ID(id),
    queryFn: () => emailTemplateService.getById(id),
    enabled: !!id,
  });

export const useEmailVariables = () =>
  useQuery({
    queryKey: QUERY_KEYS.EMAIL_TEMPLATES.VARIABLES,
    queryFn: emailTemplateService.getVariables,
    // The registry only changes with a code deploy, not user action.
    staleTime: Infinity,
  });

export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: EmailTemplatePayload) => emailTemplateService.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EMAIL_TEMPLATES.ALL }),
  });
};

export const useUpdateEmailTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<EmailTemplatePayload> }) =>
      emailTemplateService.update(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EMAIL_TEMPLATES.ALL }),
  });
};

export const useDeleteEmailTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => emailTemplateService.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EMAIL_TEMPLATES.ALL }),
  });
};
